import type { DashboardExtra, NguonTien } from '@qlk/shared';
import { NGUON_TIEN_LABEL } from '@qlk/shared';
import { Link } from 'react-router-dom';

import {
  Avatar,
  DataTable,
  EmptyState,
  Money,
  StatusBadge,
  type CotBang,
  type Tone,
} from '../../../ui/index.js';

/**
 * Bảng kênh của trang Tổng quan — năm kênh đóng góp nhiều nhất trong kỳ.
 *
 * Tiêu đề đổi theo cột cuối của bảng (xem `tieuDe` bên dưới): "Kênh theo công
 * ty giữ lại", "Kênh của bạn", hay "Kênh theo thực nhận" khi khối chia không có
 * số của kỳ.
 *
 * Thêm hai thứ so với bảng cũ: **chip nguồn tiền** (tiền kênh này đi đường
 * nào) và **thanh tỷ trọng**. Bản cũ in lượt xem và RPM cạnh thực nhận, ba con
 * số không so được với nhau; còn câu hỏi của khối này chỉ là "tiền đến từ
 * đâu, bao nhiêu phần".
 */

/* `topThucNhan` la **tuy chon** tu 31/10/2026 (muc S5: may chu bo han ca danh
   sach voi nguoi khong co `revenue:view` — ca thu tu nam dong cung la mot con
   so tien). `NonNullable` de kieu cua mot dong khong doi. */
type Dong = NonNullable<DashboardExtra['topThucNhan']>[number];

const TONE_NGUON: Record<NguonTien, Tone> = {
  network: 'info',
  adsense: 'teal',
  'chua-kiem-tien': 'neutral',
};

export interface BangTopThucNhanProps {
  duLieu: DashboardExtra['topThucNhan'] | undefined;
  dangTai: boolean;
  loi?: string | null;
  /** Nhãn kỳ đang xem, ghi vào tiêu đề khối: "28 ngày qua". */
  nhanKy: string;
  /**
   * Người đang xem có thấy phần chia đầy đủ hay không — lấy từ
   * `DashboardExtra.chiaNhanSu.xemDayDu`.
   *
   * `undefined` = khối chia không có số của kỳ này ⇒ bảng giữ nguyên bốn cột cũ
   * thay vì bày hai cột rỗng.
   */
  xemChiaDayDu?: boolean;
}

/** Ô tiền của một cột chia; `null` = kênh không có dòng chia nào ⇒ `—`. */
function OChia({ giaTri, am }: { giaTri: number | null | undefined; am?: boolean }) {
  if (giaTri === null || giaTri === undefined) return <span className="text-chu-mo">—</span>;
  return (
    <Money value={giaTri} className={`so-lieu ${am === true && giaTri < 0 ? 'text-giam' : ''}`} />
  );
}

const COT: CotBang<Dong>[] = [
  {
    key: 'kenh',
    header: 'Kênh',
    ghim: true,
    width: '34%',
    render: (k) => (
      <Link to={`/kenh/${k.channelId}`} className="flex items-center gap-2.5 hover:text-nhan">
        <Avatar name={k.name} color={k.avatarColor} size={28} />
        <span className="truncate font-semibold">{k.name}</span>
      </Link>
    ),
  },
  {
    key: 'nguon',
    header: 'Nguồn tiền',
    width: '22%',
    render: (k) => (
      <StatusBadge tone={TONE_NGUON[k.nguonTien]} cham={false}>
        {k.partnerName ?? NGUON_TIEN_LABEL[k.nguonTien]}
      </StatusBadge>
    ),
  },
  {
    key: 'tyTrong',
    header: 'Tỷ trọng',
    width: '26%',
    render: (k) => (
      <span className="flex items-center gap-2">
        <span className="h-1.5 min-w-10 flex-1 overflow-hidden rounded-full bg-neutral-nen">
          <span
            className="block h-full rounded-full bg-nhan"
            style={{ width: `${String(Math.max(k.percent, 1))}%` }}
          />
        </span>
        <span className="so-lieu w-12 shrink-0 text-right text-phu text-chu-phu">
          {k.percent.toLocaleString('vi-VN', { maximumFractionDigits: 1 })}%
        </span>
      </span>
    ),
  },
  {
    key: 'thucNhan',
    header: 'Thực nhận',
    align: 'right',
    render: (k) => <Money value={k.companyNet} className="so-lieu font-semibold" />,
  },
];

/**
 * Hai cột chia thêm vào sau "Thực nhận", theo người đang xem.
 *
 * Máy chủ quyết định: `chiaNhanSu` / `congTyGiuLai` **không có trong JSON** của
 * người xem hẹp, nên bảng đọc cờ `xemDayDu` của khối `chiaNhanSu` để chọn tập
 * cột chứ không tự suy quyền. Cột "Tỷ trọng" bị bỏ ở bản có cột chia: bảy cột
 * trong một thẻ nửa trang thì cột nào cũng hẹp, mà thanh tỷ trọng là cột ít
 * thông tin nhất.
 */
function cotChia(xemDayDu: boolean): CotBang<Dong>[] {
  return xemDayDu
    ? [
        {
          key: 'chiaNhanSu',
          header: 'Chia nhân sự',
          align: 'right',
          render: (k) => <OChia giaTri={k.chiaNhanSu} />,
        },
        {
          key: 'congTyGiuLai',
          header: 'Công ty giữ lại',
          align: 'right',
          render: (k) => <OChia giaTri={k.congTyGiuLai} am />,
        },
      ]
    : [
        {
          key: 'pctCuaToi',
          header: '% của bạn',
          align: 'right',
          render: (k) =>
            k.pctCuaToi === null ? (
              <span className="text-chu-mo">—</span>
            ) : (
              <span className="so-lieu">
                {k.pctCuaToi.toLocaleString('vi-VN', { maximumFractionDigits: 2 })}%
              </span>
            ),
        },
        {
          key: 'phanCuaToi',
          header: 'Phần của bạn',
          align: 'right',
          render: (k) => <OChia giaTri={k.phanCuaToi} />,
        },
      ];
}

export function BangTopThucNhan({
  duLieu,
  dangTai,
  loi,
  nhanKy,
  xemChiaDayDu,
}: BangTopThucNhanProps) {
  /* Có cột chia thì bỏ "Tỷ trọng" — xem `cotChia`. */
  const cot: CotBang<Dong>[] =
    xemChiaDayDu === undefined
      ? COT
      : [...COT.filter((c) => c.key !== 'tyTrong'), ...cotChia(xemChiaDayDu)];

  /*
   * Tiêu đề theo **cột cuối** của bảng, vì đó là cột người ta đọc trước:
   * người xem đủ kết ở "Công ty giữ lại", người xem hẹp chỉ thấy kênh của mình.
   * Khối chia không có số của kỳ (`undefined`) thì bảng vẫn là bảng thực nhận.
   */
  const tieuDe =
    xemChiaDayDu === undefined
      ? 'Kênh theo thực nhận'
      : xemChiaDayDu
        ? 'Kênh theo công ty giữ lại'
        : 'Kênh của bạn';

  return (
    <section
      className="overflow-hidden rounded-the border border-vien bg-be-mat"
      data-khoi="top-thuc-nhan"
    >
      <div className="flex items-center justify-between gap-3 border-b border-vien-trong px-5 py-3.5">
        <h2 className="text-tieu-de-khoi font-semibold text-chu">
          {tieuDe} · {nhanKy.toLowerCase()}
        </h2>
        <Link to="/doanh-thu" className="text-phu font-semibold text-nhan hover:text-nhan-dam">
          Doanh thu →
        </Link>
      </div>

      {loi !== null && loi !== undefined ? (
        <EmptyState loi title="Không tải được bảng kênh" description={loi} />
      ) : (
        /* Bảng nằm trong một thẻ đã có viền nên bỏ viền riêng của `DataTable`
           bằng cách để nó chiếm trọn thẻ: viền kép nhìn ra ngay ở khổ hẹp. */
        <div className="[&>div]:rounded-none [&>div]:border-0">
          <DataTable
            columns={cot}
            rows={duLieu ?? []}
            keyOf={(k) => k.channelId}
            dangTai={dangTai}
            /*
              Caption nói ra **cách chọn** năm dòng, không chỉ cách sắp: máy chủ
              chọn theo thực nhận (`/them` trả `topThucNhan`), còn thứ tự hiện
              theo cột cuối. Thiếu câu này thì "Kênh theo công ty giữ lại" dễ bị
              đọc thành "năm kênh giữ lại nhiều nhất" — hai tập có thể khác nhau.
            */
            caption={`Năm kênh thực nhận cao nhất trong ${nhanKy.toLowerCase()}${
              xemChiaDayDu === true ? ', sắp theo công ty giữ lại' : ''
            }`}
            khiRong={
              <EmptyState
                title="Kỳ này chưa kênh nào có thực nhận"
                description="Nhập sao kê Network hoặc chờ đồng bộ doanh thu."
              />
            }
          />
        </div>
      )}
    </section>
  );
}
